import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronsLeft, ChevronsRight, Loader } from "lucide-react";
import "./admin.css";

/* ============================================================
   Layout admin partagé
   Utilisé par Profile.jsx et par toutes les prochaines pages admin.
   Une page fournit seulement : son menu (groups), sa carte du haut
   (side) et son contenu (children).
   ============================================================ */

/* ---------- Helpers ---------- */

export const initials = (u) =>
  `${u?.name?.[0] || ""}${u?.surname?.[0] || ""}`.toUpperCase() || "?";

export const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "N/A";

export const todayISO = () => new Date().toISOString().split("T")[0];

/* Teinte stable calculée à partir du nom (pour les avatars colorés) */
const hueFrom = (str = "") =>
  [...str].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);

/* Sidebar réduite : mémorisée pendant la session du navigateur */
const COLLAPSE_KEY = "adm-side-collapsed";

export const useSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(COLLAPSE_KEY) === "1";
    } catch (e) {
      return false;
    }
  });

  const toggle = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch (e) {
        /* stockage indisponible : on ignore */
      }
      return next;
    });

  return [collapsed, toggle];
};

/* ---------- Petits composants ---------- */

export const Pill = ({ tone = "ok", children }) => (
  <span className={`adm-pill ${tone}`}>{children}</span>
);

export const StatusBadge = ({ status }) => (
  <Pill tone={status === "paid" ? "ok" : "warn"}>{status === "paid" ? "Paid" : "Pending"}</Pill>
);

export const Avatar = ({ user, large }) => {
  const hue = hueFrom(`${user?.name || ""}${user?.surname || ""}`);
  return (
    <div
      className={`adm-avatar ${large ? "lg" : ""}`}
      style={{
        background: `hsla(${hue}, 70%, 60%, 0.16)`,
        borderColor: `hsla(${hue}, 70%, 65%, 0.4)`,
        color: `hsl(${hue}, 80%, 72%)`,
      }}
    >
      {initials(user)}
    </div>
  );
};

export const Empty = ({ children }) => <div className="adm-empty">{children}</div>;

export const Spinner = ({ children }) => (
  <Empty>
    <Loader className="adm-spinner" /> {children}
  </Empty>
);

export const Skeleton = ({ rows = 4 }) => (
  <div className="adm-skel" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }).map((_, i) => (
      <div className="adm-skel-row" key={i} />
    ))}
  </div>
);

/* ---------- Popup : fond cliquable + touche Échap ---------- */

export const AdminPopup = ({ onClose, onEscape, children }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      (onEscape || onClose)?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onEscape, onClose]);

  return (
    <div
      className="adm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="adm-popup" role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
};

/* ---------- Layout sidebar + contenu ---------- */

export const AdminLayout = ({ collapsed, side, title, headStart, headEnd, children }) => (
  <div className={`adm-layout ${collapsed ? "collapsed" : ""}`}>
    <aside className="adm-side">{side}</aside>
    <main className="adm-main">
      <div className="adm-main-head">
        {headStart}
        <h3>{title}</h3>
        {headEnd}
      </div>
      <div className="adm-body">{children}</div>
    </main>
  </div>
);

/* groups : [{ label, items: [[clé, libellé, Icône], ...] }]
   counts : { clé: nombre } → petit compteur à droite de l'onglet */
export const SideNav = ({ groups, active, onSelect, counts = {}, collapsed }) => (
  <nav className="adm-nav" role="tablist" aria-orientation="vertical">
    {groups.map((group) => (
      <div className="adm-nav-group" key={group.label}>
        <div className="adm-nav-label">{group.label}</div>
        {group.items.map(([key, label, Icon]) => (
          <button
            key={key}
            role="tab"
            aria-selected={active === key}
            title={collapsed ? label : undefined}
            className={`adm-nav-item ${active === key ? "active" : ""}`}
            onClick={() => onSelect(key)}
          >
            <Icon />
            <span className="adm-nav-text">{label}</span>
            {counts[key] > 0 && <span className="adm-nav-count">{counts[key]}</span>}
          </button>
        ))}
      </div>
    ))}
  </nav>
);

/* Bouton "Collapse" en bas de la sidebar (bureau uniquement) */
export const CollapseButton = ({ collapsed, onToggle }) => (
  <button
    className="adm-collapse-btn"
    onClick={onToggle}
    title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
    aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
  >
    {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
    <span className="adm-collapse-text">Collapse</span>
  </button>
);

/* ---------- Confirmation (remplace window.confirm) ---------- */

export const ConfirmDialog = ({
  open,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  danger = true,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  return (
    <div className="adm-modal-overlay" onMouseDown={onCancel}>
      <div
        className="adm-confirm"
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`adm-confirm-icon ${danger ? "danger" : ""}`}>
          <AlertTriangle size={18} />
        </div>
        <h4>{title}</h4>
        <p>{message}</p>
        <div className="adm-confirm-actions">
          <button className="adm-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`adm-btn ${danger ? "danger-solid" : "primary"}`}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};